/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  StyleProp,
  ViewStyle,
  FlatList,
  TouchableOpacity,
  View,
} from "react-native";
import { useLazyQuery } from "@apollo/client";

import { useSelector } from "react-redux";
import { RootState } from "store/types";

import {
  upduoPrimaryBlack,
  upduoBlueBackground,
  upduoLightestGray,
  upduoMediumGray,
} from "config/UpduoColors";
import { GET_DISTRICT_LEADERBOARD } from "graphql/internal/Queries";
import {
  GetDistrictLeaderboardQuery,
  UserType,
} from "graphql/internal/generated";
import { LoadingDots, ProfileImage, styles, Text } from "UpduoLibrary";
import { dashboardStyles } from "./shared_components";
import * as UpduoAnalytics from "config/UpduoAnalytics";
import {
  getNodes,
  isNotMaintenanceUser,
  sortByKeyFunction,
} from "config/Utilities";

export default function ActivityLogWidget({
  style,
}: {
  style: StyleProp<ViewStyle>;
}) {
  const [leaderboardData, setLeaderboardData] = useState<
    LeaderboardEntryType[]
  >([]);

  const selfObject = useSelector((state: RootState) => state.selfObject);

  type LeaderboardEntryType = Pick<
    UserType,
    | "id"
    | "firstName"
    | "lastName"
    | "title"
    | "profileImgLgUrl"
    | "profileImgSmUrl"
    | "currMonthSessionCount"
  > & { rank?: string | number };

  const placeHolderLeaderboardData: LeaderboardEntryType[] = [
    {
      id: selfObject.userId,
      firstName: selfObject.firstName,
      lastName: selfObject.lastName,
      title: selfObject.title,
      profileImgLgUrl: selfObject.profileImgLgUrl,
      profileImgSmUrl: selfObject.profileImgSmUrl,
      rank: "-" as string | number,
      //@ts-ignore
      currMonthSessionCount: selfObject.currMonthSessionCount,
    },
  ];

  const [
    getUserDistrict,
    {
      loading: getDistrictLeaderboardLoading,
      data: getDistrictLeaderboardData,
    },
  ] = useLazyQuery<GetDistrictLeaderboardQuery>(GET_DISTRICT_LEADERBOARD, {
    fetchPolicy: "network-only", // if this isn't specified, the onCompleted will only run once, since subsequent calls will be cached
    onCompleted: ({ district }) => {
      if (district) {
        _handleGetDistrictLeaderboard();
      } else {
        setLeaderboardData(placeHolderLeaderboardData);
      }
    },
    onError: (error) => {
      setLeaderboardData(placeHolderLeaderboardData);
      console.error("ERROR getUserDistrict", error);
      UpduoAnalytics.logEvent("app-district-getuserdistrict-error", {
        error,
      });
    },
  });

  useEffect(() => {
    if (!getDistrictLeaderboardLoading && selfObject.districtId) {
      getUserDistrict({
        variables: {
          getUserDistrictRequest: {
            districtId: selfObject.districtId,
          },
        },
      });
    }
  }, [selfObject.districtId]);

  const _handleGetDistrictLeaderboard = useCallback(() => {
    // Retrieve district info for District tab
    // Get district info from the server
    const district = getDistrictLeaderboardData?.district;

    const activeUserList = getNodes(district?.activeUserList);

    const data = activeUserList.filter(isNotMaintenanceUser).map((item) => ({
      ...item,
      rank: undefined as string | number | undefined,
    }));

    // Sort the activeUserList array by score (greatest to least)
    // and then alphabetically by first name
    sortByKeyFunction(
      data,
      (x) => -x.currMonthSessionCount,
      (x) => x.firstName
    );

    // Add ranking (same rank for identical scores, "-" for zero scores)
    let currentCount: number | string | null = null;
    let currentRank: number | string | null = null;
    for (let i = 0; i < data.length; i++) {
      if (data[i].currMonthSessionCount === 0) {
        currentRank = "-";
      } else if (currentRank === null) {
        currentRank = 1;
      } else if (
        typeof currentRank === "number" &&
        typeof currentCount === "number" &&
        data[i].currMonthSessionCount < currentCount
      ) {
        currentRank++;
      }
      currentCount = data[i].currMonthSessionCount;
      data[i]["rank"] = currentRank;
    }
    setLeaderboardData(data);
  }, [getDistrictLeaderboardData]);

  /*
  const _handleShowExploreModal = (item) => {
    setLoadingEntry(item.id);
    if (item.firstName) {
      UpduoAnalytics.logEvent("user-home-explore-show", {
        tab: "district",
      });
      dispatch(
        ActionCreators.setExploreObject({
          id: item.id,
          firstName: item.firstName,
          lastName: item.lastName,
          primaryEmail: item.primaryEmail,
          title: item.title,
          profileImgLgUrl: item.profileImgLgUrl,
          currMonthSessionCount: item.currMonthSessionCount,
          district: getDistrictLeaderboardData?.district,
          rank: item.rank,
        })
      );
      onShowExploreModal();
      setLoadingEntry(null);
    } else {
      return;
    }
  };
  */

  return (
    <View style={[{ padding: 20 }, dashboardStyles.dashboardWidget, style]}>
      <Text style={dashboardStyles.widgetTitle}>{"Leaderboard"}</Text>
      <View
        style={{
          marginTop: 20,
          marginBottom: 10,
          marginLeft: 10,
          marginRight: 20,
          flexDirection: "row",
        }}
      >
        <Text>{"Rank"}</Text>
        <View style={{ width: 48 }} />
        <Text>{"Name"}</Text>
        <View style={{ flexGrow: 1 }} />
        <Text>{"Sessions"}</Text>
      </View>
      {getDistrictLeaderboardLoading ? (
        <LoadingDots />
      ) : (
        <FlatList
          scrollEnabled={true}
          keyExtractor={(_, index) => String(index)}
          data={leaderboardData}
          renderItem={({ item }) => (
            <TouchableOpacity
              onPress={() => {
                // _handleShowExploreModal(item);
              }}
            >
              <DistrictEntry
                item={item}
                highlight={item.id === selfObject.userId}
                isLoading={false}
              />
            </TouchableOpacity>
          )}
        />
      )}
    </View>
  );
}

interface DistrictEntryProps {
  item: any;
  isLoading: boolean;
  highlight: boolean;
}

function DistrictEntry({ item, isLoading, highlight }: DistrictEntryProps) {
  return (
    <View
      style={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-start",
        alignItems: "center",
        backgroundColor: highlight ? upduoBlueBackground : undefined,
        borderTopColor: upduoLightestGray,
        borderTopWidth: 1,
        borderStyle: "solid",
      }}
    >
      <Text
        style={{
          width: 15,
          fontSize: 16,
          color: upduoPrimaryBlack,
          marginLeft: 20,
          marginRight: 16,
        }}
      >
        {item.rank}
      </Text>
      <ProfileImage
        style={styles.thumbnailSmallPhotoSquared}
        userObject={item}
        size={40}
        round={false}
      />
      <View
        style={{
          marginHorizontal: 10,
          marginVertical: 14,
          display: "flex",
          flexDirection: "column",
          flexShrink: 1,
        }}
      >
        <Text
          style={{
            fontSize: 16,
            color: upduoPrimaryBlack,
          }}
          numberOfLines={1}
          ellipsizeMode={"tail"}
        >
          {`${item.firstName} ${item.lastName}`}
        </Text>
        <Text
          style={{
            fontSize: 12,
            color: upduoMediumGray,
            paddingTop: 7,
          }}
          numberOfLines={1}
          ellipsizeMode={"tail"}
        >
          {item.title ? item.title : "-"}
        </Text>
      </View>
      <View
        style={{
          flexBasis: 0,
          flexGrow: 1,
        }}
      />
      {isLoading ? (
        <LoadingDots size={30} />
      ) : (
        <Text
          style={{
            fontSize: 16,
            marginRight: 20,
          }}
        >
          {item.currMonthSessionCount}
        </Text>
      )}
    </View>
  );
}
